<!-- Reusable type glyph -->
<ng-template #ic let-t="t">
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    @switch (t) {
      @case ('account') {
        <path d="M3 21h18M6 21V7l6-4 6 4v14M10 9h.01M14 9h.01M10 13h.01M14 13h.01M10 17h.01M14 17h.01"/>
      }
      @case ('contact') {
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      }
      @case ('sample') {
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"/>
      }
      @case ('quote') {
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
      }
      @default {
        <circle cx="9" cy="21" r="1"/>
        <circle cx="20" cy="21" r="1"/>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
      }
    }
  </svg>
</ng-template>

<div class="gs-palette" role="dialog" aria-label="Global search" (keydown)="onKeydown($event)">
  <!-- Search field -->
  <div class="search-row">
    @if (iskeywordSearchLoading) {
      <span class="lead spinner" aria-hidden="true"></span>
    } @else {
      <svg class="lead" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <path d="M21 21l-4.35-4.35"/>
      </svg>
    }
    <input #input type="text" [(ngModel)]="term" (ngModelChange)="onInput()"
           placeholder="Search accounts, contacts, samples, quotes or orders"
           autocomplete="off" spellcheck="false" aria-label="Search the console"/>
    @if (term) {
      <button type="button" class="clear" (click)="clear()" aria-label="Clear">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
             stroke-linecap="round">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    }
    <button type="button" class="esc" (click)="close()">esc</button>
    <!-- Touch devices: the `esc` chip names a key they don't have and is hidden
         there, so they get a real close button in its place. -->
    <button type="button" class="close-touch" aria-label="Close search" (click)="close()">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
           stroke-linecap="round">
        <path d="M18 6L6 18M6 6l12 12"/>
      </svg>
    </button>
  </div>

  <!-- Scope chips -->
  <div class="scopes" #scopeBar>
    @for (s of scopes; track s.key) {
      <!-- A pressed-state button group, deliberately NOT `role="tablist"`: that
           role promises arrow-key navigation between the tabs, and ↑↓ already
           belong to the result list (the palette advertises them in its footer).
           Claiming the role without the keys is worse for a screen-reader user
           than not claiming it.

           The count is announced as part of the name, so it is not visual-only
           — `aria-label` replaces the label text here, which is why it repeats
           it. Absent while a count is 0, exactly as the badge is. -->
      <button type="button" class="chip" [class.on]="scope === s.key"
              [attr.aria-pressed]="scope === s.key"
              [attr.aria-label]="term && counts[s.key] > 0 ? s.label + ', ' + counts[s.key] + ' results' : s.label"
              (click)="setScope(s.key)">
        {{ s.label }}
        @if (term && counts[s.key] > 0) {
          <span class="chip-count">{{ counts[s.key] }}</span>
        }
      </button>
    }
  </div>

  <!-- Results -->
  <div class="results" (scroll)="onScroll($event)">
    @if (!term) {
      <div class="hint">
        <div class="hint-ic">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
        </div>
        <p class="hint-title">Search across the whole console</p>
        <!-- `indexNote` carries its own leading em dash and is empty until the count
             arrives, so the sentence is complete at every moment. -->
        <p class="hint-sub">Find any account, contact, sample request, quote or order{{ indexNote }}.</p>
      </div>
    } @else if (hitCount > 0) {
      <!-- Grouped hits -->
      @for (row of rows; track $index) {
        @if (row.kind === 'header') {
          <div class="group-head">{{ row.label }}</div>
        } @else {
          <button type="button" class="hit" [id]="'gs-hit-' + row.index"
                  [class.active]="activeIndex === row.index"
                  (mouseenter)="activeIndex = row.index" (click)="selectEvent(row.hit)">
            <span class="hit-ic" [attr.data-type]="row.hit.type">
              <ng-container [ngTemplateOutlet]="ic" [ngTemplateOutletContext]="{t: row.hit.type}"/>
            </span>
            <!-- an account / contact hit can be right-clicked to copy its name (and
                 any email or phone the sub-line carries) -->
            <span class="hit-body" [gcCopy]="row.hit.type === 'account' || row.hit.type === 'contact' ? row.hit.title : null">
              <span class="hit-title">
                @for (seg of row.hit.segments; track $index) {
                  @if (seg.hit) {
                    <mark>{{ seg.text }}</mark>
                  } @else {
                    <span>{{ seg.text }}</span>
                  }
                }
              </span>
              @if (row.hit.subtitle) {
                <span class="hit-sub">{{ row.hit.subtitle }}</span>
              }
            </span>
            @if (row.hit.ref) {
              <span class="hit-ref" [attr.data-type]="row.hit.type">{{ row.hit.ref }}</span>
            }
            <svg class="enter-hint" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 10l-5 5 5 5"/>
              <path d="M20 4v7a4 4 0 0 1-4 4H4"/>
            </svg>
          </button>
        }
      }
      @if (iskeywordSearchLoading) {
        <div class="more-loading"><span class="spinner"></span></div>
      }
    } @else if (iskeywordSearchLoading || !searched) {
      <!-- Loading the first page for this term -->
      <div class="hint">
        <div class="hint-ic"><span class="spinner"></span></div>
        <p class="hint-title">Searching…</p>
        <p class="hint-sub">Looking for “{{ term }}” across the console.</p>
      </div>
    } @else {
      <!-- No results -->
      <div class="hint">
        <div class="hint-ic">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35M8 11h6"/>
          </svg>
        </div>
        <p class="hint-title">No matches for “{{ term }}”</p>
        <p class="hint-sub">Try a name, project, or a reference like O-WEB-3641.</p>
      </div>
    }
  </div>

  <!-- Footer hints -->
  <div class="foot">
    <span><kbd>↑↓</kbd> navigate</span>
    <span><kbd>↵</kbd> open</span>
    <span><kbd>esc</kbd> close</span>
    <span class="foot-right"><kbd>⌘</kbd><kbd>K</kbd> toggle</span>
  </div>
</div>
